/**
 * Runs uploads a few at a time and reports each one as it settles.
 *
 * Every upload surface here does the same three-step dance per file: ask the API
 * for a signed URL, POST the bytes straight to Cloudflare, then register the
 * result. Done in a `for await` loop, thirty photos is ninety serialized round
 * trips, which is slow enough that people assume it broke. Done all at once, a
 * phone's worth of photos opens thirty parallel connections and Cloudflare
 * starts refusing them.
 *
 * A small concurrency window is the middle: fast enough to feel like a batch,
 * polite enough to finish. One file failing never cancels the rest — its error
 * is reported against that file and the queue carries on.
 */

export interface UploadOutcome<T> {
  index: number;
  /** Present when the upload succeeded. */
  value?: T;
  /** Present when it failed. Never both. */
  error?: Error;
}

export interface RunUploadsOptions {
  /**
   * How many run at once. Four keeps a batch moving without tripping
   * Cloudflare's per-client limits or starving the rest of the page.
   */
  concurrency?: number;
}

export const DEFAULT_UPLOAD_CONCURRENCY = 4;

/**
 * Applies `upload` to every item, at most `concurrency` at a time.
 *
 * Resolves once every item has settled, with outcomes in the original order so
 * a caller can line them up against its own file list. Never rejects: a thrown
 * error becomes that item's `error`.
 */
export async function runUploads<TItem, TResult>(
  items: readonly TItem[],
  upload: (item: TItem, index: number) => Promise<TResult>,
  options: RunUploadsOptions = {},
): Promise<Array<UploadOutcome<TResult>>> {
  const limit = Math.max(1, options.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY);
  const outcomes: Array<UploadOutcome<TResult>> = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        outcomes[index] = { index, value: await upload(items[index], index) };
      } catch (error) {
        outcomes[index] = {
          index,
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return outcomes;
}

/** Cloudflare Images rejects anything larger in a single request. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export interface FileScreenResult<TFile> {
  accepted: TFile[];
  /** Files rejected before any network call, with the reason to show the user. */
  rejected: Array<{ file: TFile; reason: string }>;
}

/**
 * Drops files the upload would reject anyway.
 *
 * Worth doing before the queue starts, not because it saves a request, but
 * because Cloudflare's rejection surfaces as a generic failure. A modern phone
 * photo clears 10 MB routinely, so "that one is too big" is a far more useful
 * thing to say than "couldn't upload".
 */
export function screenImageFiles<TFile extends { name: string; size: number; type: string }>(
  files: readonly TFile[],
  maxBytes: number = MAX_IMAGE_BYTES,
): FileScreenResult<TFile> {
  const accepted: TFile[] = [];
  const rejected: Array<{ file: TFile; reason: string }> = [];

  for (const file of files) {
    if (file.type && !file.type.startsWith("image/")) {
      rejected.push({ file, reason: "not an image" });
      continue;
    }
    if (file.size > maxBytes) {
      const limitMb = Math.round(maxBytes / (1024 * 1024));
      rejected.push({ file, reason: `over ${limitMb} MB` });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}
